import React, { useState, useEffect } from 'react';
import { X, Globe, Users, Mail, Plus, Trash2, Video } from 'lucide-react';
import { api } from '../../lib/api';
import type { WorkspaceMember } from '@sahaj/types';

interface CreateMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  onCreated: () => void;
}

export const CreateMeetingModal: React.FC<CreateMeetingModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  onCreated,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  );
  
  const [workspaceMembers, setWorkspaceMembers] = useState<WorkspaceMember[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [guestEmailInput, setGuestEmailInput] = useState('');
  const [guestEmails, setGuestEmails] = useState<string[]>([]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && workspaceId) {
      api.workspaces
        .listMembers(workspaceId)
        .then((members: any) => setWorkspaceMembers(members || []))
        .catch(() => setWorkspaceMembers([]));

      // Default start/end dates
      const now = new Date();
      now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15);
      const later = new Date(now.getTime() + 60 * 60 * 1000);

      setStartDate(now.toISOString().split('T')[0] || '');
      setStartTime(now.toTimeString().slice(0, 5) || '');
      setEndDate(later.toISOString().split('T')[0] || '');
      setEndTime(later.toTimeString().slice(0, 5) || '');
      setError(null);
    }
  }, [isOpen, workspaceId]);

  if (!isOpen) return null;

  const handleAddGuestEmail = () => {
    const email = guestEmailInput.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (guestEmails.includes(email)) {
      setError('Guest email already added');
      return;
    }
    setGuestEmails([...guestEmails, email]);
    setGuestEmailInput('');
    setError(null);
  };

  const handleRemoveGuestEmail = (emailToRemove: string) => {
    setGuestEmails(guestEmails.filter((e) => e !== emailToRemove));
  };

  const toggleUserSelection = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      setSelectedUserIds(selectedUserIds.filter((id) => id !== userId));
    } else {
      setSelectedUserIds([...selectedUserIds, userId]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Meeting title is required');
      return;
    }

    if (!startDate || !startTime || !endDate || !endTime) {
      setError('Start date, start time, end date, and end time are required');
      return;
    }

    const startObj = new Date(`${startDate}T${startTime}`);
    const endObj = new Date(`${endDate}T${endTime}`);

    if (isNaN(startObj.getTime()) || isNaN(endObj.getTime())) {
      setError('Invalid start or end date/time');
      return;
    }

    if (endObj <= startObj) {
      setError('Meeting end time must be after start time');
      return;
    }

    const startIso = startObj.toISOString();
    const endIso = endObj.toISOString();

    setIsLoading(true);
    setError(null);

    try {
      const payload: any = {
        title: title.trim(),
        meetingType: 'SCHEDULED',
        timezone,
        startTime: startIso,
        endTime: endIso,
        participantUserIds: selectedUserIds,
        externalGuestEmails: guestEmails,
      };
      if (description.trim()) payload.description = description.trim();

      await api.meetings.create(workspaceId, payload);

      onCreated();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to create meeting');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-card">
          <div className="flex items-center gap-2">
            <Video className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">New Meeting</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="p-3 text-xs bg-destructive/15 text-destructive rounded-lg border border-destructive/20">
              {error}
            </div>
          )}



          {/* Title & Description */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">
              Meeting Title *
            </label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Q3 Roadmap Review"
              className="w-full px-3 py-2 text-sm bg-muted/40 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">
              Description (Optional)
            </label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Agenda or context for participants..."
              className="w-full px-3 py-2 text-sm bg-muted/40 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          {/* Schedule Inputs */}
          <div className="space-y-3 p-3 bg-muted/20 border border-border rounded-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-muted-foreground mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs bg-card border border-border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-muted-foreground mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs bg-card border border-border rounded-md"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-muted-foreground mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs bg-card border border-border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-muted-foreground mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs bg-card border border-border rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                  <Globe className="w-3 h-3" /> Timezone
                </label>
                <input
                  type="text"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-xs bg-card border border-border rounded-md"
                />
              </div>
            </div>

          {/* Internal Workspace Participants */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> Workspace Participants
            </label>
            <div className="max-h-36 overflow-y-auto space-y-1.5 border border-border rounded-lg p-2 bg-muted/20">
              {workspaceMembers.length === 0 ? (
                <div className="text-xs text-muted-foreground py-2 text-center">
                  No workspace members found
                </div>
              ) : (
                workspaceMembers.map((m) => {
                  const isSelected = selectedUserIds.includes(m.user.id);
                  return (
                    <button
                      type="button"
                      key={m.user.id}
                      onClick={() => toggleUserSelection(m.user.id)}
                      className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-left text-xs transition ${
                        isSelected ? 'bg-primary/20 text-foreground font-medium' : 'hover:bg-muted text-muted-foreground'
                      }`}
                    >
                      <span className="truncate">{m.user.displayName}</span>
                      <span className="text-[10px] uppercase font-bold opacity-60">{isSelected ? 'Added' : '+ Add'}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* External Guests */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
              <Mail className="w-3.5 h-3.5" /> External Email Guests
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="email"
                placeholder="guest@external-company.com"
                value={guestEmailInput}
                onChange={(e) => setGuestEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddGuestEmail();
                  }
                }}
                className="flex-1 px-3 py-1.5 text-xs bg-muted/40 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <button
                type="button"
                onClick={handleAddGuestEmail}
                className="px-3 py-1.5 text-xs bg-muted hover:bg-muted/80 text-foreground rounded-lg font-medium transition flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>

            {guestEmails.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {guestEmails.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-card border border-border text-xs rounded-full text-foreground"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => handleRemoveGuestEmail(email)}
                      className="text-muted-foreground hover:text-destructive transition"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Footer Submit */}
          <div className="pt-3 border-t border-border flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg transition shadow-sm disabled:opacity-50"
            >
              {isLoading ? 'Creating...' : 'Schedule Meeting'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
